import React, { useState } from "react";
import { AlertCircle, MapPin, Users, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import SubmitCountdown from "./SubmitCountdown";

const STATUS_SEQUENCE = [
  {
    key: "on_way_to_pickup",
    label: "On way to pickup",
    info: "Location Required",
    description:
      "Your location will be captured when you tap the button below.",
    nextLabel: "Arrived on Location",
    nextIcon: MapPin,
  },
  {
    key: "arrived_on_location",
    label: "Arrived on Location",
    info: "Location Confirmed",
    description: "You have reached the pickup location.",
    nextLabel: "Passenger On Board",
    nextIcon: Users,
  },
  {
    key: "passenger_onboard",
    label: "Passenger On Board",
    info: "Journey Started",
    description: "Passenger has boarded the vehicle.",
    nextLabel: "Click to complete passenger drop",
    nextIcon: CheckCircle,
  },
  {
    key: "passenger_drop_successful",
    label: "Click to complete passenger drop",
    info: "Upload Photos Required",
    description: "Please upload 1-2 completion photos.",
    nextLabel: "Click to complete passenger drop",
    nextIcon: CheckCircle,
  },
];

const StatusFlow = ({
  currentStatus = null,
  onStatusUpdate,
  isLoading,
  onFinalPhotoRequired,
}) => {
  const [notes, setNotes] = useState("");

  // Map currentStatus: null → "on_way_to_pickup"
  // This ensures the first step is always "on_way_to_pickup"
  const validStatus = currentStatus || "on_way_to_pickup";

  const getStatusIndex = (status) => {
    return STATUS_SEQUENCE.findIndex((s) => s.key === status);
  };

  const currentIndex = getStatusIndex(validStatus);

  // Check if ride is completed (all steps done)
  const isRideCompleted =
    currentIndex === STATUS_SEQUENCE.length - 1 &&
    currentStatus === "passenger_drop_successful";

  const currentStatusInfo = STATUS_SEQUENCE[currentIndex];
  const nextStatusInfo = STATUS_SEQUENCE[currentIndex + 1];
  const totalSteps = STATUS_SEQUENCE.length;

  // Determine the status to send on button click
  let statusToSend;
  if (currentStatus === null) {
    statusToSend = "on_way_to_pickup";
  } else {
    statusToSend = STATUS_SEQUENCE[currentIndex + 1]?.key;
  }

  const statusToSendInfo = STATUS_SEQUENCE.find((s) => s.key === statusToSend);
  const NextIcon = statusToSendInfo?.nextIcon;

  const handleStatusSubmit = async () => {
    if (statusToSend === "passenger_drop_successful") {
      // Trigger photo capture modal instead of directly updating
      onFinalPhotoRequired(statusToSend, notes);
      return;
    }

    try {
      if (!statusToSend) {
        toast.error("No status available");
        return;
      }

      await onStatusUpdate(statusToSend, notes);
      toast.success("Status updated successfully");
      setNotes("");
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error(err.message || "Failed to update status");
    }
  };

  // Show Ride Completed page
  if (isRideCompleted) {
    return (
      <div className="w-full space-y-6 text-center">
        <div className="space-y-4">
          <div className="flex justify-center">
            <CheckCircle className="w-24 h-24 text-green-500" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Ride Completed! 🎉
          </h2>
          <p className="text-lg text-gray-600">
            Thank you for completing this ride.
          </p>
        </div>

        {/* Summary Box */}
        {/* <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-green-800">
            <span className="font-semibold">Status:</span> Passenger drop
            successful
          </p>
          <p className="text-sm text-green-800">
            <span className="font-semibold">All photos submitted:</span> ✓
          </p>
        </div> */}

        {/* Completion Message */}
        {/* <div className="space-y-3">
          <p className="text-gray-600">
            Your trip is now complete. Safe travels on your next ride!
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Back to Home
          </button>
        </div> */}
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Current Status Title */}
      {/* <h2 className="text-3xl font-bold text-gray-900 text-center">
        {currentStatusInfo?.label}
      </h2> */}

      {/* Info Box */}
      {/* <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-yellow-800">
            {currentStatusInfo?.info}
          </p>
          <p className="text-yellow-700 text-sm mt-1">
            {currentStatusInfo?.description}
          </p>
        </div>
      </div> */}

      {/* Notes Input */}
      <div className="space-y-2">
        <label className="text-sm font-semibold text-gray-700">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes for this status update"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows="3"
        />
      </div>

      {/* Action Button */}
      <button
        onClick={handleStatusSubmit}
        disabled={isLoading}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-3 text-lg"
      >
        {isLoading ? (
          <SubmitCountdown label="Updating" iconClassName="w-6 h-6" />
        ) : (
          <>
            <NextIcon className="w-6 h-6" />
            {statusToSend === "passenger_drop_successful"
              ? statusToSendInfo?.nextLabel
              : `${statusToSendInfo?.label}`}
          </>
        )}
      </button>

      {/* Step Indicator */}
      {/* <div className="flex items-center justify-center gap-2 text-gray-600">
        <span className="text-sm">⏱</span>
        <span className="text-sm font-medium">
          Step {currentIndex + 1} of {totalSteps}
        </span>
      </div> */}

      {/* Tap Instructions */}
      {/* <p className="text-center text-gray-600 text-sm">
        Tap when you {currentStatusInfo?.label.toLowerCase()}
      </p> */}
    </div>
  );
};

export default StatusFlow;
